/**
 * v4.58.0 — Indicador de presión por tipo de unidad (READ-ONLY).
 * Sección consumida por Disponibilidad.js.
 * No bloquea ventas. Solo lectura. RBAC: admin/admin_vendedor/logistica/vendedor_logistica.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { availabilityAPI } from "../lib/api";
import { toast } from "sonner";
import { AlertCircle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";

const LEVEL_CONFIG = {
  ok: { color: "bg-green-100 text-green-800 border-green-300", label: "Holgura" },
  warning: { color: "bg-yellow-100 text-yellow-800 border-yellow-300", label: "Presión alta" },
  critical: { color: "bg-orange-100 text-orange-800 border-orange-300", label: "Casi sin holgura" },
  oversold: { color: "bg-red-100 text-red-800 border-red-300", label: "Sobreventa" },
};

const formatDt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const presetRanges = () => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  const tomorrowEnd = new Date(todayEnd.getTime() + 24 * 60 * 60 * 1000);
  const week = new Date(todayStart.getTime() + 7 * 24 * 60 * 60 * 1000);
  const month = new Date(todayStart.getTime() + 30 * 24 * 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().slice(0, 16);
  return {
    today: { start: fmt(todayStart), end: fmt(todayEnd) },
    tomorrow: { start: fmt(tomorrowStart), end: fmt(tomorrowEnd) },
    week7: { start: fmt(todayStart), end: fmt(week) },
    month30: { start: fmt(todayStart), end: fmt(month) },
  };
};

export const PressureByType = () => {
  const presets = useMemo(() => presetRanges(), []);
  const [range, setRange] = useState(presets.week7);
  const [activePreset, setActivePreset] = useState("week7");
  const [includeQuotes, setIncludeQuotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    try {
      const res = await availabilityAPI.pressureByType({
        start_datetime: range.start.length === 16 ? range.start + ":00" : range.start,
        end_datetime: range.end.length === 16 ? range.end + ":00" : range.end,
        include_quotes: includeQuotes,
      });
      setData(res.data);
    } catch (e) {
      console.error("[PressureByType] load error:", e.response?.status, e.response?.data);
      toast.error(e.response?.data?.detail || "Error al cargar presión por tipo");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.start, range.end, includeQuotes]);

  const applyPreset = (key) => {
    setActivePreset(key);
    if (key !== "custom") setRange(presets[key]);
  };

  return (
    <Card className="mt-6" data-testid="pressure-by-type-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg">Presión por tipo de unidad</CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Lectura informativa. No bloquea ventas ni modifica disponibilidad.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Incluir cotizaciones</Label>
            <Switch checked={includeQuotes} onCheckedChange={setIncludeQuotes} data-testid="pressure-toggle-quotes" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          {["today", "tomorrow", "week7", "month30"].map((k) => (
            <Button
              key={k}
              size="sm"
              variant={activePreset === k ? "default" : "outline"}
              onClick={() => applyPreset(k)}
              data-testid={`pressure-preset-${k}`}
            >
              {k === "today" ? "Hoy" : k === "tomorrow" ? "Mañana" : k === "week7" ? "Próx. 7 días" : "Próx. 30 días"}
            </Button>
          ))}
          <Button
            size="sm"
            variant={activePreset === "custom" ? "default" : "outline"}
            onClick={() => setActivePreset("custom")}
            data-testid="pressure-preset-custom"
          >
            Personalizado
          </Button>
          {activePreset === "custom" && (
            <>
              <div>
                <Label className="text-xs">Inicio</Label>
                <Input
                  type="datetime-local"
                  value={range.start}
                  onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
                  data-testid="pressure-custom-start"
                />
              </div>
              <div>
                <Label className="text-xs">Fin</Label>
                <Input
                  type="datetime-local"
                  value={range.end}
                  onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
                  data-testid="pressure-custom-end"
                />
              </div>
              <Button size="sm" onClick={load} data-testid="pressure-apply-custom">Aplicar</Button>
            </>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Calculando presión…
          </div>
        )}

        {!loading && data && (
          <>
            {data.unmapped_types && data.unmapped_types.length > 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2" data-testid="pressure-unmapped">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Tipos no normalizados detectados:</strong> {data.unmapped_types.join(", ")}.
                  Datos sucios — limpiar manualmente. No afecta cálculo de los tipos válidos.
                </div>
              </div>
            )}

            {data.by_type.length === 0 ? (
              <p className="text-sm text-slate-500">Sin demanda en este periodo.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pressure-table">
                  <thead>
                    <tr className="border-b text-left text-xs text-slate-500">
                      <th className="p-2">Tipo</th>
                      <th className="p-2 text-right">Solic.</th>
                      <th className="p-2 text-right">v2</th>
                      <th className="p-2 text-right">Pendientes</th>
                      <th className="p-2 text-right">Legacy</th>
                      <th className="p-2 text-right">Mtto</th>
                      <th className="p-2 text-right">Cap. ef.</th>
                      <th className="p-2 text-right">Disp.</th>
                      <th className="p-2 text-right">%</th>
                      <th className="p-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_type.map((row) => {
                      const cfg = LEVEL_CONFIG[row.level] || LEVEL_CONFIG.ok;
                      const isOpen = !!expanded[row.type];
                      return (
                        <React.Fragment key={row.type}>
                          <tr
                            className="border-b hover:bg-slate-50 cursor-pointer"
                            onClick={() => setExpanded((e) => ({ ...e, [row.type]: !e[row.type] }))}
                            data-testid={`pressure-row-${row.type}`}
                          >
                            <td className="p-2 font-medium flex items-center gap-1">
                              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              {row.type_label}
                            </td>
                            <td className="p-2 text-right font-semibold">{row.demand_total}</td>
                            <td className="p-2 text-right text-slate-500">{row.demand_v2}</td>
                            <td className="p-2 text-right text-slate-500">{row.demand_pending_lines}</td>
                            <td className="p-2 text-right text-slate-500">{row.demand_legacy_v1}</td>
                            <td className="p-2 text-right text-slate-500">{row.in_maintenance}</td>
                            <td className="p-2 text-right">{row.capacity_effective}</td>
                            <td className={`p-2 text-right font-bold ${row.available_estimated < 0 ? "text-red-600" : "text-green-700"}`}>
                              {row.available_estimated}
                            </td>
                            <td className="p-2 text-right">{row.pressure_pct}%</td>
                            <td className="p-2">
                              <Badge className={`text-[10px] ${cfg.color}`} variant="outline">{cfg.label}</Badge>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={10} className="p-3 bg-slate-50">
                                {row.contracts_with_demand.length === 0 ? (
                                  <p className="text-xs text-slate-500">Sin contratos contribuyentes.</p>
                                ) : (
                                  <div className="space-y-1 text-xs" data-testid={`pressure-detail-${row.type}`}>
                                    <div className="font-semibold text-slate-700 mb-2">Contratos contribuyentes:</div>
                                    {row.contracts_with_demand.map((c, i) => (
                                      <div key={i} className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-[9px]">{c.source}</Badge>
                                        <span className="font-mono text-slate-600">{c.folio}</span>
                                        <span>{c.client_name}</span>
                                        <span className="text-slate-400">×{c.count}</span>
                                        <span className="text-slate-400 ml-auto">
                                          {formatDt(c.start_datetime)} → {formatDt(c.end_datetime)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
